import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import RatingMeter from "@/components/ui/RatingMeter";
import {
  atLeastBand,
  formatRating,
  ratingBandInfo,
} from "@/lib/ratingScale";
import useRatingConfig from "@/hooks/useRatingConfig";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  Eye,
  Edit,
  Trash2,
  UserCog,
  Download,
  Upload,
  Users,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  Lock,
  Sparkles,
  Star,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ShieldCheck,
  MoreHorizontal,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import AssignManagerDialog from "@/components/admin/AssignManagerDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  formatDate,
  formatCurrency,
  getStatusColor,
  debounce,
  generateEmployeeId,
  calculateGrossSalary,
  formatEnumLabel,
} from "@/lib/utils";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { useOnboarding } from "@/contexts/OnboardingContext";

import {
  getEmployees,
  getEmployeeRating,
  getEmployeeLimitStatus,
  deleteEmployees,
  bulkDeleteEmployees,
} from "@/services/adminService";
import { downloadSampleTemplate } from "@/lib/utils";
import BuySeatsDialog from "@/components/admin/BuySeatsDialog";
import RequestSeatsDialog from "@/components/admin/RequestSeatsDialog";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationPageSize,
} from "@/components/ui/pagination";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { fetchAllChangeRequests } from "@/services/employeeChangeRequestService";
import { usePermission } from "@/contexts/RoleAccessContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EmployeeChangeRequests from "./EmployeeChangeRequests";
import { createCanHelpers } from "@/utils/rbac";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;

/**
 * Admin-side palette for each performance band, keyed by the band key from
 * @/lib/ratingScale. Presentation only — this table deliberately holds NO
 * thresholds and NO labels, so it cannot drift away from the shared band logic
 * the way its predecessor did.
 */
const BAND_STYLES = {
  outstanding: {
    bg: "from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20",
    border: "border-green-200 dark:border-green-700",
    text: "text-green-600 dark:text-green-400",
    star: "fill-green-500 text-green-500",
    starInactive: "text-green-200 dark:text-green-800",
    icon: "bg-green-100 dark:bg-green-800",
    emoji: "🌟",
  },
  excellent: {
    bg: "from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
    border: "border-blue-200 dark:border-blue-700",
    text: "text-blue-600 dark:text-blue-400",
    star: "fill-blue-500 text-blue-500",
    starInactive: "text-blue-200 dark:text-blue-800",
    icon: "bg-blue-100 dark:bg-blue-800",
    emoji: "✨",
  },
  good: {
    bg: "from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20",
    border: "border-yellow-200 dark:border-yellow-700",
    text: "text-yellow-600 dark:text-yellow-400",
    star: "fill-yellow-500 text-yellow-500",
    starInactive: "text-yellow-200 dark:text-yellow-800",
    icon: "bg-yellow-100 dark:bg-yellow-800",
    emoji: "👍",
  },
  satisfactory: {
    bg: "from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20",
    border: "border-orange-200 dark:border-orange-700",
    text: "text-orange-600 dark:text-orange-400",
    star: "fill-orange-500 text-orange-500",
    starInactive: "text-orange-200 dark:text-orange-800",
    icon: "bg-orange-100 dark:bg-orange-800",
    emoji: "👌",
  },
  needs_improvement: {
    bg: "from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20",
    border: "border-red-200 dark:border-red-700",
    text: "text-red-600 dark:text-red-400",
    star: "fill-red-500 text-red-500",
    starInactive: "text-red-200 dark:text-red-800",
    icon: "bg-red-100 dark:bg-red-800",
    emoji: "⚠️",
  },
};

const EmployeeList = () => {
  const navigate = useNavigate();
  const { hasFeature, planId, planName,isPaidOnline, planSlug } = useSubscription();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  // "all" | "none" (no manager) | a manager's name
  const [managerFilter, setManagerFilter] = useState("all");
  const [assignManagerDialog, setAssignManagerDialog] = useState({
    open: false,
    mode: "selected",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    employee: null,
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false });
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [exporting, setExporting] = useState(false);
  // Profile change requests live as a tab on this page rather than a separate
  // sidebar entry — they are part of managing employees.
  // A profile-change notification deep-links here with the approvals view open.
  const [viewingApprovals, setViewingApprovals] = useState(
    location.state?.tab === "change-requests",
  );
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [employeeRatings, setEmployeeRatings] = useState({});
  const [ratingDialog, setRatingDialog] = useState({
    open: false,
    employee: null,
    rating: null,
  });
  const { canAddEmployees } = useOnboarding();
  const { can } = usePermission();
  const { canCreate, canEdit, canDelete } = createCanHelpers(can);

  // Check if bulk import is enabled
  const canBulkImport = hasFeature("bulk_import");
  const canCreateEmployee = canCreate("employess");
  const canEditEmployee = canEdit("employees");
  const canViewChangeRequests = can("employee_change_requests", "view");
  const canDeleteEmployee = canDelete("employees");

  // Employee limit state
  const [employeeLimit, setEmployeeLimit] = useState(null);
  const [buySeatsOpen, setBuySeatsOpen] = useState(false);
  const [requestSeatsOpen, setRequestSeatsOpen] = useState(false);

  // Admin-configured rating scale. Bands are anchored to base_rating, so both
  // base and max are needed — sourcing them separately is what let this screen
  // drift out of step with the employee's own view.
  //
  // The seat-limit response carries the scale and is NOT subscription-gated, so
  // it seeds the bands on first paint; useRatingConfig then supplies the full
  // rating_config (penalties, bonuses) that the reference panel below renders.
  const ratingLimitScale = useMemo(
    () => ({
      base_rating: employeeLimit?.baseRating,
      max_rating: employeeLimit?.totalRating,
    }),
    [employeeLimit?.baseRating, employeeLimit?.totalRating],
  );
  const { config: ratingConfig, scale: ratingScale } =
    useRatingConfig(ratingLimitScale);
  const ratingMax = ratingScale.max;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // rows per page

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // reset to first page when page size changes
  };

  // Fetch employee limit status
  const fetchEmployeeLimit = async () => {
    try {
      const response = await getEmployeeLimitStatus();
      console.log("getEmployeeLimitStatus", response);

      setEmployeeLimit(response);
    } catch (error) {
      console.error("Error fetching employee limit:", error);
    }
  };

  useEffect(() => {
    fetchEmployeeLimit();
    fetchEmployees();
  }, []);

  // Pending count for the header badge, so it is accurate before the approvals
  // view has ever been opened. Once open, that view reports fresh counts via
  // onCountChange — including after an approve or reject — so this runs once.
  useEffect(() => {
    if (!canViewChangeRequests) return;
    // Deep-linked straight into the approvals view: it fetches on mount
    // anyway, so skip the duplicate request.
    if (viewingApprovals) return;

    let cancelled = false;

    (async () => {
      try {
        // Server-side status filter, so the row count IS the pending count.
        const rows = await fetchAllChangeRequests("pending");
        if (!cancelled) {
          setPendingRequestCount(Array.isArray(rows) ? rows.length : 0);
        }
      } catch (error) {
        // The badge is informational — never let it disturb the page.
        if (!isAuthExpiryError(error)) {
          console.error("Failed to load pending approval count:", error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewChangeRequests]);

  useEffect(() => {
    // 💡 Reset to first page on filter/search change
    setCurrentPage(1);
    filterEmployees();
  }, [employees, searchTerm, statusFilter, departmentFilter, managerFilter]);
  
  // NOTE: Original useEffect [searchTerm, statusFilter, departmentFilter] is removed 
  // because the logic is now inside the [employees, searchTerm, statusFilter, departmentFilter] useEffect
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [searchTerm, statusFilter, departmentFilter]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // The whole company — the API otherwise returns the first 100.
      const response = await getEmployees(undefined, "all");
      setEmployees(response);
      console.log("setEmployees", response);
      // Fetch ratings for all employees
      // fetchAllRatings(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  // const fetchAllRatings = ... (commented out in original)
  // const fetchAllRatings = ... (commented out in original)

  const handleViewRating = async (employee) => {
    const rating = employeeRatings[employee.employee_id];
    setRatingDialog({ open: true, employee, rating });
  };

  // Palette only — keyed by band so the admin colour language survives, while
  // the LABEL and the THRESHOLDS come from @/lib/ratingScale.
  //
  // This used to be a second band table with its own cut-offs (0.9/0.8/0.7/0.6)
  // and labels shifted a whole band relative to the shared module, which is why
  // an employee read one grade higher here than in their own drawer.
  //
  // Keyed by band rather than by tone because tone collapses Outstanding and
  // Excellent into a single "positive", losing the green/blue distinction.
  const ratingStyle = (rating) => {
    const info = ratingBandInfo(rating, ratingScale);
    return { ...BAND_STYLES[info.key], label: info.label, info };
  };

  const isPendingEmployee = (emp) => {
    return (
      emp.status === "active" &&
      (!emp.salary_structure || emp.salary_structure.length === 0)
    );
  };

  const filterEmployees = debounce(() => {
    let filtered = employees;

    // 🔍 SEARCH FILTER
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          emp.name.toLowerCase().includes(term) ||
          emp.employee_id.toLowerCase().includes(term) ||
          emp.email.toLowerCase().includes(term) ||
          emp.department.toLowerCase().includes(term) ||
          emp.designation.toLowerCase().includes(term)
      );
    }

    // 🟡 STATUS FILTER (including dynamic pending logic)
    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        // Show employees missing salary structure
        filtered = filtered.filter((emp) => isPendingEmployee(emp));
      } else if (statusFilter === "active") {
        // Active = only employees WITH salary structure
        filtered = filtered.filter(
          (emp) => emp.status === "active" && !isPendingEmployee(emp)
        );
      } else {
        filtered = filtered.filter((emp) => emp.status === statusFilter);
      }
    }

    // 🏢 DEPARTMENT FILTER
    if (departmentFilter !== "all") {
      filtered = filtered.filter((emp) => emp.department === departmentFilter);
    }

    // 👤 MANAGER FILTER (manager_name is null when there's no active manager)
    if (managerFilter === "none") {
      filtered = filtered.filter((emp) => !emp.manager_name);
    } else if (managerFilter !== "all") {
      filtered = filtered.filter((emp) => emp.manager_name === managerFilter);
    }

    setFilteredEmployees(filtered);
  }, 300);

  const handleDeleteEmployee = async (employeeId) => {
    try {
      await deleteEmployees(employeeId);
      toast.success("Employee deleted successfully");
      setDeleteDialog({ open: false, employee: null });
      fetchEmployees();
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to update employee");

      console.error("Error deleting employee:", error);
      toast.error(errorMessage);
    }
  };

  // Bulk delete functions
  const handleSelectAll = (checked) => {
    // 💡 Only select employees currently visible in the filtered/sorted list
    if (checked) {
      const allIds = new Set(paginatedEmployees.map((emp) => emp._id));
      setSelectedEmployees(allIds);
    } else {
      setSelectedEmployees(new Set());
    }
  };

  const handleSelectEmployee = (employeeId, checked) => {
    const newSelection = new Set(selectedEmployees);
    if (checked) {
      newSelection.add(employeeId);
    } else {
      newSelection.delete(employeeId);
    }
    setSelectedEmployees(newSelection);
  };

  const handleBulkDelete = async () => {
    if (selectedEmployees.size === 0) {
      toast.error("No employees selected");
      return;
    }

    setDeleting(true);
    try {
      const employee_ids = Array.from(selectedEmployees);
      const response = await bulkDeleteEmployees(employee_ids);

      toast.success(`${response.deleted_count} employees deleted successfully`);

      if (response.errors && response.errors.length > 0) {
        response.errors.forEach((error) => {
          toast.error(error);
        });
      }

      setBulkDeleteDialog({ open: false });
      setSelectedEmployees(new Set());
      fetchEmployees();
    } catch (error) {
      console.error("Error bulk deleting employees:", error);
      toast.error(error.response?.data?.message || "Failed to delete employees");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddEmployeeClick = async () => {
    try {
      if (!employeeLimit) {
        toast.error("Unable to load employee limit. Try again.");
        return;
      }

      // CASE 1: Allowed
      if (employeeLimit.allowed && employeeLimit.reason === "ok") {
        navigate("/employees/new");
        return;
      }

      // OFF-PLATFORM companies (super-admin provisioned, no Razorpay) can't
      // self-serve buy seats. They request more seats from the super-admin.
      // The dialog itself handles both cases: within the plan cap it lets them
      // request more seats; at the cap it explains an upgrade is needed.
      if (!isPaidOnline) {
        if (
          employeeLimit.reason === "seats_limit_reached" ||
          employeeLimit.reason === "plan_limit_exceeded"
        ) {
          setRequestSeatsOpen(true);
          return;
        }
        toast.error(employeeLimit.message || "Cannot add more employees.");
        return;
      }

      // ONLINE companies self-serve below.
      // CASE 2: Paid seats exhausted but still within the plan cap → buy more
      // seats inline (prorated at the plan's per-user rate).
      if (employeeLimit.reason === "seats_limit_reached") {
        setBuySeatsOpen(true);
        return;
      }

      // CASE 3: Plan cap hit → an add-on can't cross the cap; go upgrade the
      // plan. Deep-link into the upgrade flow, which preselects the next tier.
      if (employeeLimit.reason === "plan_limit_exceeded") {
        navigate(`/subscription`, {
          state: {
            reason: "plan_limit_exceeded",
          }
        });
        return;
      }

      toast.error(employeeLimit.message || "Cannot add more employees.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to check employee limit");
    }
  };

  const clearSelection = () => {
    setSelectedEmployees(new Set());
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    // 💡 Reset to first page on sort change
    setCurrentPage(1);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="w-4 h-4" />
    ) : (
      <ArrowDown className="w-4 h-4" />
    );
  };

  const getNestedValue = (obj, path) => {
    const keys = path.split(".");
    let value = obj;
    for (let key of keys) {
      value = value?.[key];
    }
    return value || "";
  };

  const sortedEmployees = useMemo(() => {
    const isPendingEmployee = (emp) =>
      emp.status === "active" &&
      (!emp.salary_structure || emp.salary_structure.length === 0);

    // People who have left (resigned / terminated) always sit at the bottom,
    // whatever the sort.
    const hasLeft = (emp) => emp.status === "resigned" || emp.status === "terminated";
    const leftLast = (a, b) => Number(hasLeft(a)) - Number(hasLeft(b));

    // First: sort pending to top (before applying your existing sorting rules)
    const base = [...filteredEmployees].sort((a, b) => {
      const left = leftLast(a, b);
      if (left) return left;

      const aPending = isPendingEmployee(a);
      const bPending = isPendingEmployee(b);

      if (aPending && !bPending) return -1; // a first
      if (!aPending && bPending) return 1; // b first

      return 0; // otherwise equal → continue normal sorting
    });

    // If no sort key selected, return pending-first ordering
    if (!sortConfig.key) return base;

    // Apply your normal sorting logic *after* pending-first ordering
    return base.sort((a, b) => {
      const left = leftLast(a, b);
      if (left) return left;

      // Special handling for rating column
      if (sortConfig.key === "rating") {
        const aRating = employeeRatings[a.employee_id]?.rating || 0;
        const bRating = employeeRatings[b.employee_id]?.rating || 0;

        if (aRating < bRating) return sortConfig.direction === "asc" ? -1 : 1;
        if (aRating > bRating) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      // Special handling for salary (computed gross)
      if (sortConfig.key === "salary") {
        const aSalary = calculateGrossSalary(a.salary_structure || {});
        const bSalary = calculateGrossSalary(b.salary_structure || {});
        if (aSalary < bSalary) return sortConfig.direction === "asc" ? -1 : 1;
        if (aSalary > bSalary) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      // Default sorting for other columns
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredEmployees, sortConfig, employeeRatings]);

  // Export functionality
  const exportEmployeeData = async () => {
    setExporting(true);
    try {
      const exportData = employees.map((emp) => ({
        "Employee ID": emp.employee_id,
        Name: emp.name,
        Email: emp.email,
        Phone: emp.phone || "",
        Gender: emp.gender || "",
        "Date of Birth": emp.date_of_birth || "",
        "Aadhar Number": emp.aadhar_number || "",
        "PAN Number": emp.pan_number || "",
        "Marital Status": emp.marital_status || "",
        Address: emp.address || "",
        // Emergency Contact & Medical Information. Column names match the
        // import's mapping exactly, so these three copy across cleanly.
        "Emergency Contact Name": emp.emergency_contact_name || "",
        "Emergency Contact Phone": emp.emergency_contact_phone || "",
        "Blood Group": emp.blood_group || "",
        Department: emp.department,
        Designation: emp.designation,
        "Date of Joining": emp.date_of_joining || "",
        "Work Location": emp.work_location || "",
        Status: emp.status,
        "Resignation Date": emp.resignation_date || "",
        "Termination Date": emp.termination_date || "",
        "Status Reason": emp.status_reason || "",
        // NOTE: Swapped Gross Salary and Net Salary in the export object 
        // to match the keys of the original object structure if they were correct
        "Net Salary": emp.net_salary || "", 
        "Gross Salary": emp.gross_salary || "", 
        "Bank Name": emp.bank_info?.bank_name || "",
        "Account Number": emp.bank_info?.account_number || "",
        "IFSC Code": emp.bank_info?.ifsc_code || "",
        Branch: emp.bank_info?.branch || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Employees");
      XLSX.writeFile(
        wb,
        `Employees_Export_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast.success("Employee data exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export employee data");
    } finally {
      setExporting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) {
      return;
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

const managerNames = [
  ...new Set((employees || []).map((emp) => emp.manager_name).filter(Boolean)),
].sort((a, b) => a.localeCompare(b));

const departments =
  employees && employees.length > 0
    ? [
        ...new Set(
          employees.map((emp) => emp.department?.trim()).filter((dep) => dep) // removes "", null, undefined
        ),
      ]
    : [];

  // 💡 PAGINATION LOGIC
  const totalItems = sortedEmployees.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  const start = (currentPage - 1) * pageSize;
  const paginatedEmployees = sortedEmployees.slice(start, start + pageSize);
  
  const handlePageChange = (page) => {
      setCurrentPage(page);
      const tableElement = document.getElementById("employee-table");
      if (tableElement) {
          tableElement.scrollIntoView({ behavior: "smooth" });
      }
      clearSelection();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-80" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 7 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // 💡 Function to render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <Pagination className="flex justify-end">
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious 
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        isActive={currentPage > 1}
                        className={currentPage <= 1 ? "pointer-events-none opacity-50" : undefined}
                    />
                </PaginationItem>
                
                {startPage > 1 && (
                    <>
                        <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                        </PaginationItem>
                        {startPage > 2 && (
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )}
                    </>
                )}

                {pageNumbers.map(page => (
                    <PaginationItem key={page}>
                        <PaginationLink 
                            isActive={page === currentPage} 
                            onClick={() => handlePageChange(page)}
                        >
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && (
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )}
                        <PaginationItem>
                            <PaginationLink onClick={() => handlePageChange(totalPages)}>{totalPages}</PaginationLink>
                        </PaginationItem>
                    </>
                )}
                
                <PaginationItem>
                    <PaginationNext 
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        isActive={currentPage < totalPages}
                        className={currentPage >= totalPages ? "pointer-events-none opacity-50" : undefined}
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
  };

  const blockIfNotOnboarded = () => {
    if (!canAddEmployees) {
      toast.warning("Complete onboarding first to manage employees.");
      navigate("/dashboard");
      return true;
    }
    return false;
  };

  return (
    <div className="space-y-6" data-testid="employee-list">
      {/* Page Header. The approvals view supplies its own title and puts a
          back link above it, so this one is list-only. */}
      {!viewingApprovals && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Employees
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Manage your organization's employees
            </p>
          </div>
          <div className="flex items-center gap-2">
            <>
              {/* Export / Template / Import collapsed into one menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="dark:border-gray-600 dark:text-gray-300"
                  >
                    {/* <MoreHorizontal className="w-4 h-4 mr-2 dark:text-gray-400" /> */}
                    Actions
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuItem
                    disabled={exporting}
                    onSelect={() => {
                      if (employees.length === 0) {
                        toast.error("No employees found");
                        return;
                      }
                      if (blockIfNotOnboarded()) return;
                      exportEmployeeData();
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {exporting ? "Exporting..." : "Export Employees"}
                  </DropdownMenuItem>

                  {/* Bulk import is a paid feature. The items stay visible but
                      disabled so the capability is discoverable, matching how
                      the buttons behaved before. */}
                  <DropdownMenuItem
                    disabled={!canBulkImport && !isPaidOnline}
                    onSelect={() => {
                      if (blockIfNotOnboarded()) return;
                      if (!canBulkImport) {
                        navigate("/subscription", {
                          state: { reason: "feature_upgrade_required" },
                        });
                        return;
                      }
                      downloadSampleTemplate(hasFeature("department_mapping"));
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                    {!canBulkImport && <Lock className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    disabled={!canBulkImport && !isPaidOnline}
                    onSelect={() => {
                      if (!canCreate("employees", true)) return;
                      if (!canBulkImport) {
                        navigate("/subscription", {
                          state: {
                            reason: "feature_upgrade_required",
                            from: location.pathname,
                            requiredFeature: "bulk_employee_import",
                            currentPlan: planName,
                            currentPlanId: planId,
                          },
                        });
                        return;
                      }
                      if (blockIfNotOnboarded()) return;
                      navigate("/employees/import", {
                        state: {
                          from: location.pathname,
                          requiredFeature: "bulk_employee_import",
                          currentPlan: planName,
                          currentPlanId: planId,
                        },
                      });
                    }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import from Excel
                    {!canBulkImport && <Lock className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>

                  {hasFeature("department_mapping") && (
                    <DropdownMenuItem
                      onSelect={() => {
                        if (blockIfNotOnboarded()) return;
                        if (!canEdit("employees", true)) return;
                        setAssignManagerDialog({ open: true, mode: "department" });
                      }}
                    >
                      <UserCog className="w-4 h-4 mr-2" />
                      Set Department Manager
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Only shown to someone who can actually review requests. */}
              {canViewChangeRequests && (
                <Button
                  variant="outline"
                  size="sm"
                  // `relative` anchors the count badge to this button's corner.
                  className="relative dark:border-gray-600 dark:text-gray-300"
                  onClick={() => setViewingApprovals(true)}
                >
                  <ShieldCheck className="w-4 h-4 mr-2 dark:text-gray-400" />
                  Profile Approvals
                  {/* Same floating-corner treatment as the notification bell,
                      in amber rather than red — this is a queue to work
                      through, not an alert. */}
                  {pendingRequestCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                      {pendingRequestCount > 9 ? "9+" : pendingRequestCount}
                    </span>
                  )}
                </Button>
              )}

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        // Keep the button clickable at the limit: off-platform
                        // companies open the "request seats" dialog, online ones
                        // open the buy-seats / upgrade flow. Only block while the
                        // limit status is still loading.
                        disabled={!employeeLimit}
                        onClick={() => {
                          if (blockIfNotOnboarded()) return;
                          if (!canCreate("employees", true)) return;
                          handleAddEmployeeClick();
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {employeeLimit &&
                        !employeeLimit.allowed &&
                        !isPaidOnline
                          ? "Request Seats"
                          : "Add Employee"}
                        {employeeLimit && !employeeLimit.allowed && (
                          <AlertCircle className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {employeeLimit && !employeeLimit.allowed && (
                    <TooltipContent>
                      <p className="font-semibold">Employee Limit Reached</p>
                      <p className="text-xs">{employeeLimit.message}</p>
                      <p className="text-xs mt-1">
                        {!isPaidOnline
                          ? "Request more seats from your administrator"
                          : "Upgrade your plan to add more employees"}
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          </div>
        </div>
      )}

      {viewingApprovals ? (
        // The approvals view renders its own header — back button beside the
        // title, same shape as Add/Edit Employee.
        <EmployeeChangeRequests
          onBack={() => setViewingApprovals(false)}
          onCountChange={setPendingRequestCount}
        />
      ) : (
        <>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{employees.length}</p>
                <p className="text-xs text-gray-500">Total Employees</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-primary rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {
                    employees.filter(
                      (emp) =>
                        emp.status === "active" &&
                        Array.isArray(emp.salary_structure) &&
                        emp.salary_structure.length > 0,
                    ).length
                  }
                </p>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {employees.filter((emp) => emp.status === "resigned").length}
                </p>
                <p className="text-xs text-gray-500">Resigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-red-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {
                    employees.filter((emp) => emp.status === "terminated")
                      .length
                  }
                </p>
                <p className="text-xs text-gray-500">Terminated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-2xl font-bold">{departments.length}</p>
                <p className="text-xs text-gray-500">Departments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Employee Directory</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search employees by name, ID, email, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resigned">Resigned</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="exit_scheduled">Exit Scheduled</SelectItem>
                </SelectContent>
              </Select>
              {hasFeature("department_mapping") && (
                <Select
                  value={departmentFilter}
                  onValueChange={setDepartmentFilter}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={managerFilter} onValueChange={setManagerFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managerNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedEmployees.size > 0 && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  {selectedEmployees.size} employee
                  {selectedEmployees.size === 1 ? "" : "s"} selected
                </span>
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  <X className="w-4 h-4 mr-2" />
                  Clear Selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (blockIfNotOnboarded()) return;
                    if (!canEdit("employees", true)) return;
                    setAssignManagerDialog({ open: true, mode: "selected" });
                  }}
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Assign Manager
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (blockIfNotOnboarded()) return;
                    if (!canDelete("employees", true)) return;
                    setBulkDeleteDialog({ open: true });
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </div>
          )}

          {/* Employee Table */}
          <div
            id="employee-table"
            className="border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 dark:border-gray-700">
                  <TableHead className="w-12 text-gray-700 dark:text-gray-300">
                    <Checkbox
                      checked={
                        paginatedEmployees.length > 0 && // 💡 Check based on paginated employees
                        paginatedEmployees.every((emp) =>
                          selectedEmployees.has(emp._id),
                        )
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Employee</span>
                      {getSortIcon("name")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                    onClick={() => handleSort("employee_id")}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Employee ID</span>
                      {getSortIcon("employee_id")}
                    </div>
                  </TableHead>
                  {hasFeature("department_mapping") && (
                    <TableHead
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                      onClick={() => handleSort("department")}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Job Details</span>
                        {getSortIcon("department")}
                      </div>
                    </TableHead>
                  )}
                  <TableHead className="text-gray-700 dark:text-gray-300">
                    Manager
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Status</span>
                      {getSortIcon("status")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                    onClick={() => handleSort("salary")}
                  >
                    <div className="flex items-center space-x-2">
                      <span>Salary</span>
                      {getSortIcon("salary")}
                    </div>
                  </TableHead>
                  {hasFeature("performance_ratings") && (
                    <TableHead
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                      onClick={() => handleSort("rating")}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Rating</span>
                        {getSortIcon("rating")}
                      </div>
                    </TableHead>
                  )}

                  <TableHead className="text-right text-gray-700 dark:text-gray-300">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* 🔄 LOADING SKELETON */}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={10} className="py-10">
                      <div className="flex flex-col items-center gap-4">
                        <div className="flex gap-3">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                          <span className="text-gray-600 dark:text-gray-300">
                            Loading employees...
                          </span>
                        </div>

                        {/* Skeleton rows */}
                        <div className="w-full space-y-3 px-10">
                          {[1, 2, 3].map((i) => (
                            <div
                              key={i}
                              className="h-8 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                            ></div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && paginatedEmployees.length === 0 ? (
                  <TableRow className="border-b border-gray-200 dark:border-gray-700">
                    <TableCell
                      colSpan={10}
                      className="text-center py-8 text-gray-500 dark:text-gray-400"
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <Users className="h-8 w-8 text-gray-400" />
                        <p className="text-gray-500">No employees found</p>
                        {employees.length === 0 ? (
                          <Button
                            onClick={() => {
                              if (blockIfNotOnboarded()) return;
                              if (!canCreate("employees", true)) return;
                              handleAddEmployeeClick();
                            }}
                            size="sm"
                          >
                            Add your first employee
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSearchTerm("");
                              setStatusFilter("active");
                              setDepartmentFilter("all");
                            }}
                          >
                            Clear filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  !loading &&
                  paginatedEmployees.map((employee) => {
                    // 💡 Iterate over paginatedEmployees
                    const hasNoSalary =
                      !employee.salary_structure ||
                      employee.salary_structure.length === 0;

                    const isPending =
                      employee.status === "active" && hasNoSalary;

                    return (
                      <React.Fragment key={employee.id}>
                        {/* 🔶 PENDING WARNING ROW */}
                        {isPending && (
                          <>
                            <TableRow className="bg-amber-50/40 dark:bg-amber-900/10">
                              {/* 1 — Checkbox */}
                              <TableCell>
                                <Checkbox
                                  checked={selectedEmployees.has(employee._id)}
                                  onCheckedChange={(checked) =>
                                    handleSelectEmployee(employee._id, checked)
                                  }
                                />
                              </TableCell>

                              {/* 2 — Employee (name + email) */}
                              <TableCell>
                                <div className="flex items-center space-x-3">
                                  <Avatar>
                                    <AvatarFallback className="bg-primary/10 dark:bg-primary/20 text-primary">
                                      {getInitials(employee.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium text-gray-900 dark:text-gray-200">
                                      {employee.name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {employee.email}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>

                              {/* 3 — Employee ID */}
                              <TableCell className="font-mono text-gray-800 dark:text-gray-300">
                                {employee.employee_id}
                              </TableCell>

                              {/* 4 — REPLACE ALL OTHER COLUMNS WITH CARD */}
                              <TableCell
                                colSpan={
                                  hasFeature("department_mapping") ? 8 : 7
                                }
                              >
                                <div className="flex items-center justify-between gap-4 p-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40">
                                  {/* Left: Warning */}
                                  <div className="flex items-center space-x-3">
                                    <AlertTriangle className="text-amber-600 dark:text-amber-400 h-5 w-5" />
                                    <h3 className="font-semibold text-amber-700 dark:text-amber-300 text-sm">
                                      Salary Structure Missing — Employee
                                      Pending
                                    </h3>
                                  </div>

                                  {/* Right: Actions */}
                                  <div className="flex items-center gap-2">
                                    {/* Fix */}
                                    <Button
                                      size="sm"
                                      className={`${
                                        !canEditEmployee
                                          ? "opacity-70 cursor-not-allowed hover:bg-primary"
                                          : "bg-amber-600 hover:bg-amber-700 text-white"
                                      }`}
                                      onClick={() => {
                                        if (!canEdit("employees", true)) return;

                                        navigate(
                                          `/employees/${employee.employee_id}/edit`,
                                        );
                                      }}
                                    >
                                      Fix
                                    </Button>

                                    {/* Delete */}
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => {
                                        if (blockIfNotOnboarded()) return;
                                        if (!canDelete("employees", true)) return;
                                        setDeleteDialog({
                                          open: true,
                                          employee,
                                        });
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {!isPending && (
                          <TableRow
                            key={employee.id}
                            className="border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-muted/50"
                            onClick={() => {
                              if (blockIfNotOnboarded()) return;
                              navigate("/employees/view", {
                                state: {
                                  id: employee.employee_id,
                                  empId: employee._id,
                                },
                              });
                            }}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedEmployees.has(employee._id)}
                                onCheckedChange={(checked) =>
                                  handleSelectEmployee(employee._id, checked)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-3">
                                <Avatar>
                                  <AvatarFallback className="bg-primary/10 dark:bg-primary/20 text-primary">
                                    {getInitials(employee.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-gray-200">
                                    {employee.name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {employee.email}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-gray-800 dark:text-gray-300">
                              {employee.employee_id}
                            </TableCell>
                            {hasFeature("department_mapping") && (
                              <TableCell>
                                <div>
                                  <div className="font-medium text-sm text-gray-900 dark:text-gray-200">
                                    {employee.department || "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {employee.designation || ""}
                                  </div>
                                </div>
                              </TableCell>
                            )}
                            <TableCell className="text-sm text-gray-800 dark:text-gray-300">
                              {employee.manager_name || (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const displayStatus =
                                  employee.status === "active" && hasNoSalary
                                    ? "pending"
                                    : formatEnumLabel(employee.status);

                                return (
                                  <Badge
                                    className={`${getStatusColor(
                                      displayStatus,
                                    )} capitalize`}
                                  >
                                    {displayStatus}
                                  </Badge>
                                );
                              })()}
                            </TableCell>

                            <TableCell className="text-gray-800 dark:text-gray-300">
                              {formatCurrency(employee.gross_salary)}
                            </TableCell>
                            {hasFeature("performance_ratings") && (
                              <TableCell>
                                <div className="flex gap-x-2 items-center">
                                  {formatRating(employee?.rating, ratingScale, 2)}{" "}
                                  <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                                </div>
                              </TableCell>
                            )}

                            <TableCell
                              className="text-right"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-end space-x-2">
                                {/* View Details Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                  // onClick={() => handleViewEmployee(employee)}
                                  onClick={() => {
                                    if (blockIfNotOnboarded()) return;
                                    navigate("/employees/view", {
                                      state: {
                                        id: employee.employee_id,
                                        empId: employee._id,
                                      },
                                    });
                                  }}
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>

                                {/* Edit Employee Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary ${
                                    !canEditEmployee
                                      ? "opacity-70 cursor-not-allowed"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (blockIfNotOnboarded()) return;
                                    if (!canEdit("employees", true)) return;
                                    navigate(
                                      `/employees/${employee.employee_id}/edit`,
                                    );
                                  }}
                                  title="Edit Employee"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>

                                {/* Delete Employee Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`${
                                    !canDeleteEmployee
                                      ? "opacity-70 cursor-not-allowed hover:bg-primary"
                                      : "h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                                  }`}
                                  onClick={() => {
                                    if (blockIfNotOnboarded()) return;
                                    if (!canDelete("employees", true)) return;
                                    setDeleteDialog({ open: true, employee });
                                  }}
                                  title="Delete Employee"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {/* 💡 Pagination Component */}
          <div className="w-full flex items-center justify-between mt-4">
            <div className="flex w-full items-center gap-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {Math.min(start + 1, totalItems)} to{" "}
                {Math.min(start + pageSize, totalItems)} of {totalItems} entries
              </div>
              <PaginationPageSize
                value={pageSize}
                onValueChange={handlePageSizeChange}
              />
            </div>
            {renderPagination()}
          </div>
          {/* End Pagination Component */}
        </CardContent>
      </Card>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          !open && setDeleteDialog({ open: false, employee: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              employee
              <strong> {deleteDialog.employee?.name} </strong>
              and remove their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteEmployee(deleteDialog.employee._id)}
              data-testid="confirm-delete-btn"
            >
              Delete Employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk / department-wide manager assignment */}
      <AssignManagerDialog
        open={assignManagerDialog.open}
        onOpenChange={(open) =>
          setAssignManagerDialog((prev) => ({ ...prev, open }))
        }
        mode={assignManagerDialog.mode}
        employeeIds={Array.from(selectedEmployees)}
        initialDepartment={departmentFilter !== "all" ? departmentFilter : ""}
        onAssigned={() => {
          clearSelection();
          fetchEmployees();
        }}
      />

      {/* Bulk Delete Dialog */}
      <AlertDialog
        open={bulkDeleteDialog.open}
        onOpenChange={(open) => setBulkDeleteDialog({ open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              <span>Delete Multiple Employees</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              <strong>
                {" "}
                {selectedEmployees.size} employee
                {selectedEmployees.size === 1 ? "" : "s"}{" "}
              </strong>
              and remove their data from our servers.
              <div className="mt-3 p-3 rounded-md">
                <p className="text-sm mb-2">
                  Employees to be deleted:
                </p>
                <div className="max-h-32 overflow-y-auto">
                  {Array.from(selectedEmployees).map((employeeId) => {
                    const emp = employees.find(
                      (e) => e._id === employeeId,
                    );
                    return emp ? (
                      <div key={employeeId} className="text-sm">
                        • {emp.name} ({emp.employee_id})
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedEmployees.size} Employee
                  {selectedEmployees.size === 1 ? "" : "s"}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rating Details Modal */}
      <Dialog
        open={ratingDialog.open}
        onOpenChange={(open) =>
          setRatingDialog({
            open,
            employee: open ? ratingDialog.employee : null,
            rating: open ? ratingDialog.rating : null,
          })
        }
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3">
              {ratingDialog.rating &&
                (() => {
                  const colors = ratingStyle(ratingDialog.rating.rating);
                  return (
                    <div
                      className={`h-12 w-12 rounded-full bg-gradient-to-r ${colors.bg} border ${colors.border} flex items-center justify-center`}
                    >
                      <Star className={`h-6 w-6 ${colors.star}`} />
                    </div>
                  );
                })()}
              {!ratingDialog.rating && (
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-gray-400 to-gray-400 flex items-center justify-center">
                  <Star className="h-6 w-6 fill-white text-white" />
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Performance Rating Analysis (YTD)
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {ratingDialog.employee?.name} (
                  {ratingDialog.employee?.employee_id})
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          {ratingDialog.rating ? (
            (() => {
              // Previously called getRatingColor WITHOUT the maximum, so every
              // colour and label in this panel was computed against a max of 5
              // even on a 10-point scale — and could disagree with the header
              // above, which did pass it. The scale is now closed over, so
              // there is no argument left to forget.
              const colors = ratingStyle(ratingDialog.rating.rating);
              return (
                <div className="space-y-6 py-4">
                  {/* Current Rating Display */}
                  <div
                    className={`bg-gradient-to-r ${colors.bg} p-6 rounded-lg border ${colors.border}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <div className={`text-6xl font-bold ${colors.text}`}>
                            {colors.info.value.toFixed(3)}
                            <span className="ml-1 text-2xl font-medium text-gray-500 dark:text-gray-400">
                              / {ratingMax}
                            </span>
                          </div>
                          {/* Progress toward the configured maximum. A fixed
                              5-star row misreported the scale whenever the
                              admin set a maximum other than 5. */}
                          <RatingMeter
                            rating={ratingDialog.rating.rating}
                            scale={ratingScale}
                            showValue={false}
                            showBand={false}
                            className="mt-3"
                          />
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            Year-to-Date {ratingDialog.rating.year}
                          </p>
                        </div>

                        <div className="h-20 w-px bg-gray-300 dark:bg-gray-600"></div>

                        <div>
                          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                            {colors.emoji} {colors.label}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Based on{" "}
                            {ratingDialog.rating.details.attendance_days} days
                            attendance
                          </p>
                          {ratingDialog.rating.details
                            .progress_from_baseline !== undefined && (
                            <div
                              className={`mt-2 text-sm font-medium ${
                                ratingDialog.rating.details
                                  .progress_from_baseline >= 0
                                  ? "text-green-600 dark:text-green-400"
                                  : "text-red-600 dark:text-red-400"
                              }`}
                            >
                              {ratingDialog.rating.details
                                .progress_from_baseline >= 0
                                ? "↗"
                                : "↘"}
                              {Math.abs(
                                ratingDialog.rating.details
                                  .progress_from_baseline,
                              ).toFixed(3)}{" "}
                              from baseline (4.0)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rating Breakdown */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                      Rating Breakdown
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Base Rating */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Starting Rating
                            </p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {ratingDialog.rating.details.starting_rating?.toFixed(
                                2,
                              ) || "4.00"}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🎯</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Base rating or carried forward from previous month
                        </p>
                      </div>

                      {/* Late Arrivals Impact */}
                      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Late Arrivals
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {ratingDialog.rating.details.late_arrivals} times
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
                            <span className="text-2xl">⏰</span>
                          </div>
                        </div>
                        <p className="text-xs font-semibold text-red-600 dark:text-red-400 mt-2">
                          -
                          {(
                            ratingDialog.rating.details.late_arrivals * 0.05
                          ).toFixed(3)}{" "}
                          points (0.05 per late arrival)
                        </p>
                      </div>

                      {/* OT Hours Impact */}
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Overtime Hours
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {ratingDialog.rating.details.ot_hours}h
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                            <span className="text-2xl">💪</span>
                          </div>
                        </div>
                        <p className="text-xs font-semibold text-green-600 dark:text-green-400 mt-2">
                          +
                          {(
                            ratingDialog.rating.details.ot_hours * 0.02
                          ).toFixed(3)}{" "}
                          points (0.02 per hour)
                        </p>
                      </div>

                      {/* Punctuality Bonus */}
                      <div
                        className={`p-4 rounded-lg border ${
                          ratingDialog.rating.details.punctuality_bonus > 0
                            ? "bg-primary/10 dark:bg-primary/20 border-primary/30 dark:border-primary/40"
                            : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Punctuality Bonus
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {ratingDialog.rating.details.punctuality_bonus > 0
                                ? "✓ Earned"
                                : "✗ Not Earned"}
                            </p>
                          </div>
                          <div
                            className={`h-12 w-12 rounded-full flex items-center justify-center ${
                              ratingDialog.rating.details.punctuality_bonus > 0
                                ? "bg-primary/10 dark:bg-primary/20"
                                : "bg-gray-200 dark:bg-gray-700"
                            }`}
                          >
                            <span className="text-2xl">
                              {ratingDialog.rating.details.punctuality_bonus > 0
                                ? "🏆"
                                : "⭕"}
                            </span>
                          </div>
                        </div>
                        <p
                          className={`text-xs font-semibold mt-2 ${
                            ratingDialog.rating.details.punctuality_bonus > 0
                              ? "text-primary"
                              : "text-gray-500 dark:text-gray-400"
                          }`}
                        >
                          {ratingDialog.rating.details.punctuality_bonus > 0
                            ? `+${ratingDialog.rating.details.punctuality_bonus.toFixed(
                                3,
                              )} points (No late arrivals!)`
                            : "0.00 points (Late arrivals present)"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Performance Insights */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-5 rounded-lg border border-purple-200 dark:border-purple-700">
                    <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2 text-purple-900 dark:text-purple-300">
                      <span>💡</span>
                      <span>Performance Insights & Recommendations</span>
                    </h3>
                    <div className="space-y-3 text-sm">
                      {/* Positive Aspects */}
                      {(ratingDialog.rating.details.punctuality_bonus > 0 ||
                        ratingDialog.rating.details.ot_hours > 10 ||
                        atLeastBand(
                          ratingDialog.rating.rating,
                          ratingScale,
                          "excellent",
                        )) && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                          <p className="font-semibold text-green-700 dark:text-green-400 mb-2">
                            ✅ Strengths:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                            {ratingDialog.rating.details.punctuality_bonus >
                              0 && (
                              <li>
                                Perfect punctuality - zero late arrivals this
                                month
                              </li>
                            )}
                            {ratingDialog.rating.details.ot_hours > 10 && (
                              <li>
                                Strong commitment with{" "}
                                {ratingDialog.rating.details.ot_hours} hours of
                                overtime
                              </li>
                            )}
                            {atLeastBand(
                              ratingDialog.rating.rating,
                              ratingScale,
                              "excellent",
                            ) && (
                              // Reads the band rather than asserting a fixed
                              // "Outstanding", so this line can never contradict
                              // the badge shown above it.
                              <li>
                                {colors.label} performance - exceeding
                                expectations
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Areas for Improvement */}
                      {(ratingDialog.rating.details.late_arrivals > 0 ||
                        ratingDialog.rating.details.ot_hours < 5 ||
                        !atLeastBand(
                          ratingDialog.rating.rating,
                          ratingScale,
                          "good",
                        )) && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                          <p className="font-semibold text-amber-700 dark:text-amber-400 mb-2">
                            ⚠️ Areas for Improvement:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                            {ratingDialog.rating.details.late_arrivals > 0 && (
                              <li>
                                Reduce late arrivals (
                                {ratingDialog.rating.details.late_arrivals} this
                                month) - potential rating gain: +
                                {(
                                  ratingDialog.rating.details.late_arrivals *
                                  Number(ratingConfig?.late_arrival ?? 0)
                                ).toFixed(2)}{" "}
                                points
                              </li>
                            )}
                            {ratingDialog.rating.details.late_arrivals > 0 &&
                              ratingDialog.rating.details.punctuality_bonus ===
                                0 && (
                                <li>
                                  Achieve perfect punctuality to earn bonus: +
                                  {Number(
                                    ratingConfig?.punctuality_bonus ?? 0,
                                  ).toFixed(2)}{" "}
                                  points
                                </li>
                              )}
                            {ratingDialog.rating.details.ot_hours < 5 &&
                              !atLeastBand(
                                ratingDialog.rating.rating,
                                ratingScale,
                                "excellent",
                              ) && (
                                <li>
                                  Consider additional overtime contributions -
                                  current:{" "}
                                  {ratingDialog.rating.details.ot_hours}h (10h =
                                  +
                                  {(
                                    Number(ratingConfig?.overtime ?? 0) * 10
                                  ).toFixed(2)}{" "}
                                  rating boost)
                                </li>
                              )}
                          </ul>
                        </div>
                      )}

                      {/* Path to the configured maximum */}
                      {ratingDialog.rating.rating < ratingMax && (
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                          <p className="font-semibold text-blue-700 dark:text-blue-400 mb-2">
                            🎯 Path to Maximum Rating ({ratingMax}):
                          </p>
                          <p className="text-gray-700 dark:text-gray-300">
                            Current gap:{" "}
                            {(ratingMax - colors.info.value).toFixed(3)} points.
                            {!atLeastBand(
                              ratingDialog.rating.rating,
                              ratingScale,
                              "outstanding",
                            ) &&
                              ratingDialog.rating.details.late_arrivals > 0 && (
                                <>
                                  {" "}
                                  Eliminate late arrivals and maintain perfect
                                  punctuality to reach {ratingMax}.
                                </>
                              )}
                            {atLeastBand(
                              ratingDialog.rating.rating,
                              ratingScale,
                              "outstanding",
                            ) &&
                              ratingDialog.rating.rating < ratingMax && (
                                <>
                                  {" "}
                                  You're very close! Continue current
                                  performance to maintain top rating.
                                </>
                              )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rating System Info */}
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center space-x-2">
                      <span>ℹ️</span>
                      <span>Rating System Details</span>
                    </h4>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      {/* Driven from rating_config. These figures were
                          previously hardcoded to the legacy defaults (4.0 /
                          -0.05 / +0.02 / +0.30 / 5.0) and were therefore wrong
                          for every company on a different configuration. */}
                      <p>
                        • <strong>Base Rating:</strong> Starts at{" "}
                        {ratingScale.base} or carries forward from previous month
                      </p>
                      <p>
                        • <strong>Late Arrivals:</strong> -
                        {ratingConfig?.late_arrival ?? "—"} points per occurrence
                      </p>
                      <p>
                        • <strong>Overtime:</strong> +
                        {ratingConfig?.overtime ?? "—"} points per 30 minutes
                        (capped at maximum rating)
                      </p>
                      <p>
                        • <strong>Punctuality Bonus:</strong> +
                        {ratingConfig?.punctuality_bonus ?? "—"} points if zero
                        late arrivals
                      </p>
                      <p>
                        • <strong>Maximum Rating:</strong> {ratingMax} (capped)
                      </p>
                      <p>
                        • <strong>Performance Bands:</strong> Good from{" "}
                        {ratingScale.base} (the base rating), Excellent from{" "}
                        {ratingScale.thresholds.excellent.toFixed(2)},
                        Outstanding from{" "}
                        {ratingScale.thresholds.outstanding.toFixed(2)}
                      </p>
                      <p>
                        • <strong>System:</strong> Cumulative - rating carries
                        forward month-to-month
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="py-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                Rating data not available
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Buy more employee seats (inline, when paid seats are exhausted) */}
      <BuySeatsDialog
        open={buySeatsOpen}
        onOpenChange={setBuySeatsOpen}
        initialSeats={employeeLimit?.shortfall || 1}
        onPurchased={async () => {
          await fetchEmployeeLimit();
          navigate("/employees/new");
        }}
      />

      {/* Request more seats from super-admin (off-platform companies) */}
      <RequestSeatsDialog
        open={requestSeatsOpen}
        onOpenChange={setRequestSeatsOpen}
        initialSeats={employeeLimit?.shortfall || 1}
        planLimit={employeeLimit?.plan_limit}
        currentSeats={employeeLimit?.purchased_seats}
        planName={planName}
        onRequested={async () => {
          await fetchEmployeeLimit();
        }}
      />
    </div>
  );
};

export default EmployeeList;
